import type { LoaderFunctionArgs } from "@remix-run/node";
import { useFetcher, useNavigate } from "@remix-run/react"
import React, { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { ActionFunctionArgs, json } from "@remix-run/node"
import { sendOTPToMobile } from "~/.server/auth/otp"
import { constants } from "~/utils/constants"
import { AppBar, ButtonSubmit, InputController, Toolbar, toast, errorHandler, submitHandler, Clickable } from "@fr8/frui";
import { verifyOtpInResetPin } from "~/.server/auth/resetPin"
import { redirectIfAuthenticated } from '~/utils/auth/helper';
import { ChevronDown } from "lucide-react";
import { Errors } from "~/components/composed/indent/createIndent/indentChunks";
import CountryCodeSelect from "~/components/composed/common/countryCodeSelect";

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const action = formData.get("action") as string;
  const mobile = formData.get("mobile") as string;
  const country_code = formData.get("country_code") as string;
  // mobileWithCode is country code without zero or plus sign ex: 971544454445
  const mobileWithCode = `${country_code}${mobile}`;

  if (action === "GET_OTP") {
    const getOtpResult = await sendOTPToMobile(mobileWithCode);
    return {
      getOtp: {
        ...getOtpResult,
      },
      showOtpLayout: getOtpResult?.status
    }
  }

  if (action === "VERIFY_OTP") {
    const otp = formData.get("otp") as string;
    const country_code = formData.get("country_code") as string;
    const country_id = formData.get("country_id") as unknown as number;
    const verifyOtpResult = await verifyOtpInResetPin(
      mobile,
      otp,
      country_id,
      country_code,
    );
    return {
      verifyOtp: {
        ...verifyOtpResult,
      },
      showOtpLayout: true
    }
  }

  return {
      status: false,
      showOtpLayout: false,
      description: "Unable to proceed",
  };
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { getCommitHeaders } = await redirectIfAuthenticated(request);
  return json({}, { headers: await getCommitHeaders() });
};




const otpFormSchema = z.object({
  otp: z.string().optional(),
  mobile: z.coerce.number({
    required_error : 'Mobile number is required',
    invalid_type_error : 'Invalid mobile number'
}).refine((val) => (/^\d+$/.test(val.toString())), {
    message: 'Enter valid mobile number',
}).refine((val) => val.toString().length >=4  ,{
  message:'Mobile must contain minimum 7 digits'
}),
country_id : z.number({
  message : 'Country code is required'
}).default(1),
country_code : z.string({
 message : 'Country code is required'
}).default('971')
})
const otpDefaultValues = {
  otp: undefined,
  mobile: undefined,
  country_id : 1,
  country_code : "971"
}

const ForgotPassword = () => {

  const fetcher = useFetcher()
  const navigate = useNavigate()
  const fetcherData = fetcher?.data as { getOtp:Record<string,string>, showOtpLayout?: boolean, verifyOtp:Record<string,string> };
  const getOtpData = fetcherData?.getOtp
  const showOtpLayout = fetcherData?.showOtpLayout
  const verifyOtpData = fetcherData?.verifyOtp
  const navigateTo = verifyOtpData?.navigateTo
  const [showCountrySelectModal,setShowCountrySelectModal] = useState<boolean>()

  const form = useForm<z.infer<typeof otpFormSchema>>({
    mode: 'onChange',
    resolver: zodResolver(otpFormSchema),
    defaultValues: otpDefaultValues,
  })


  type ValidationSchema = z.infer<typeof otpFormSchema>
  const onSubmit = (values: ValidationSchema) => {
    if( showOtpLayout && (!values.otp || values?.otp?.length <6 )){
      form.setError('otp',{ message : 'Kindly enter your 6 digit OTP' })
      return
    }
    fetcher.submit({
      ...values,
      action: showOtpLayout ? "VERIFY_OTP" : "GET_OTP"
    }, {
      method: 'POST'
    })
  }

  const handleFormSubmit = submitHandler(form.handleSubmit, onSubmit, errorHandler());
  
  React.useEffect(() => {
    if (getOtpData) {
      if (getOtpData.status) {
        toast.success(getOtpData?.description);
      }
    }
    if (verifyOtpData) {
      if (verifyOtpData?.isOtpVerified) {
        toast.success(verifyOtpData?.description);
        navigate(navigateTo ? navigateTo : constants.login, {
          replace: true,
          state: {
            isOtpVerified: verifyOtpData?.isOtpVerified,
            user : verifyOtpData?.user
          }
        })
      } else{
        toast.error(verifyOtpData?.description);
        form.resetField("otp");
      }
    }
  }, [fetcher.data]);

  const toggleCountryCode = () => setShowCountrySelectModal(prev => !prev)
  const countryCodeWatcher = form.watch('country_code') 

  return (
    <div className="min-h-dvh p-4 items-center h-dvh pt-20 w-full justify-center">
      <AppBar>
        <Toolbar showBackButton onBackClick={() => navigate(-1)} />
      </AppBar>
      <div className="w-full flex flex-col max-w-md h-full mx-auto">
        <h1 className="text-base">
          {showOtpLayout 
            ? `Enter OTP sent to your mobile`
            : "Enter the registered mobile number"}
        </h1>
          <form
            onSubmit={handleFormSubmit}
            className="flex flex-col flex-1  justify-between mt-8"
            method="POST"
          >
            <div className="flex flex-col gap-2">
              <div>
                <Controller
                  control={form.control}
                  name={"mobile"}
                  render={({ field, fieldState }) => (
                    <InputController
                      field={{ ...field, value: field.value ?? "" }}
                      fieldState={fieldState}
                      type="tel"
                      autoComplete="tel"
                      label="Mobile number"
                      placeholder="Enter number"
                      maxLength={10}
                      disabled={!!showOtpLayout}
                      required
                      className="ml-4"
                      prefixIcon={
                        <Clickable
                          onClick={toggleCountryCode}
                          className="ml-4 flex flex-row items-center text-neutral-500 hover:font-bold"
                        >
                          <p className="text-xs">
                            {countryCodeWatcher ? countryCodeWatcher : "Code"}
                          </p>
                          <ChevronDown size={16} />
                        </Clickable>
                      }
                      containerStyle={`h-12`}
                    />
                  )}
                />
                <Errors error={form.formState.errors.country_code?.message}/>
              </div>
              {showOtpLayout ? (
                <Controller
                  control={form.control}
                  name={"otp"}
                  render={({ field, fieldState }) => (
                    <InputController
                      field={{ ...field, value: field.value ?? "" }}
                      fieldState={fieldState}
                      type="tel"
                      autoComplete="tel"
                      label="OTP"
                      maxLength={6}
                      required={!showOtpLayout}
                      disabled={!showOtpLayout}
                      className="text-center font-light text-xl tracking-[2rem]"
                      containerStyle={`h-12`}
                    />
                  )}
                />
              ) : null}
            </div>
          <ButtonSubmit isLoading={form.formState.isSubmitting}>
            {!showOtpLayout ? "Get OTP" : "Verify OTP"}
          </ButtonSubmit>
          {showCountrySelectModal ? (
                <CountryCodeSelect
                  onSelectCountryCode={(value) => {
                form.setValue("country_code", value.country_code);
                form.setValue("country_id", value.country_id);
                form.clearErrors("country_code");
                form.clearErrors("country_id");
                  }}
                  onClose={toggleCountryCode}
              selectedValue={form.getValues("country_code")}
                  open={showCountrySelectModal}
            />
          ) : null}
          </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
